/**
 * AnchorBox — shared anchor UI for every scope tab in ColumnMappingStep.
 *
 * Primary-scope mode exposes a PK/Mapping toggle so the user can anchor via
 * the scope's primary key OR via any relation-mapping incident to the scope.
 * Secondary-scope mode mirrors the legacy anchor bar: one column dropdown
 * backed by a relation primitive that unlocked the scope from its parent.
 *
 * When a mapping is picked as the primary anchor, the related scope's tab
 * unlocks automatically and renders this same component in secondary mode,
 * bound to the same column.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import clsx from "clsx"
import type { AnchorableMapping } from "../../types"

export type AnchorMode = "pk" | "mapping"

interface PrimaryProps {
  pkPrimitiveTitle: string
  pkColumnIndex: number // -1 when unmapped
  availableMappings: AnchorableMapping[] // empty → Mapping toggle hidden
  mode: AnchorMode
  mappingPrimitiveId: string | null
  /** Column index used when mode === 'mapping' */
  mappingColumnIndex: number
  onPkColumnChange: (colIdx: number) => void
  onMappingColumnChange: (colIdx: number) => void
  onModeChange: (mode: AnchorMode, mappingPrimitiveId: string | null) => void
}

interface SecondaryProps {
  parentScopeName: string
  anchorPrimitiveTitle: string
  columnIndex: number
  /** When true, the anchor was inherited from a mapping-mode primary anchor
   *  and is read-only here (the primary tab controls it). */
  sharedWithPrimary?: boolean
  onColumnChange: (colIdx: number) => void
}

export function AnchorBox({
  role,
  scopeName,
  headers,
  usedColumnIndices,
  isExportMode = false,
  scopeNames,
  primary,
  secondary,
}: {
  role: "primary" | "secondary"
  scopeName: string
  headers: string[]
  usedColumnIndices: Set<number>
  isExportMode?: boolean
  scopeNames?: Record<string, string>
  primary?: PrimaryProps
  secondary?: SecondaryProps
}) {
  if (role === "primary" && primary) {
    return (
      <PrimaryAnchorBox
        scopeName={scopeName}
        headers={headers}
        usedColumnIndices={usedColumnIndices}
        isExportMode={isExportMode}
        scopeNames={scopeNames ?? {}}
        {...primary}
      />
    )
  }
  if (role === "secondary" && secondary) {
    return (
      <SecondaryAnchorBox
        scopeName={scopeName}
        headers={headers}
        usedColumnIndices={usedColumnIndices}
        isExportMode={isExportMode}
        {...secondary}
      />
    )
  }
  return null
}

// ---------------------------------------------------------------------------

function PrimaryAnchorBox({
  scopeName,
  headers,
  usedColumnIndices,
  isExportMode,
  scopeNames,
  pkPrimitiveTitle,
  pkColumnIndex,
  availableMappings,
  mode,
  mappingPrimitiveId,
  mappingColumnIndex,
  onPkColumnChange,
  onMappingColumnChange,
  onModeChange,
}: PrimaryProps & {
  scopeName: string
  headers: string[]
  usedColumnIndices: Set<number>
  isExportMode: boolean
  scopeNames: Record<string, string>
}) {
  const { translate } = useTranslation()
  const hasMappings = availableMappings.length > 0
  const activeMapping =
    availableMappings.find((m) => m.primitive_id === mappingPrimitiveId) ?? null
  const relatedName = activeMapping
    ? (scopeNames[activeMapping.related_scope_id] ??
      activeMapping.related_scope_id)
    : ""

  const columnIndex = mode === "pk" ? pkColumnIndex : mappingColumnIndex

  return (
    <div className="my-2 rounded-lg border border-yellow5/30 bg-grey1 p-3">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="rounded bg-yellow1/15 px-1.5 py-0.5 text-[10px] font-medium text-yellow1">
          {translate("QMS/ANCHOR/BADGE")}
        </span>
        {mode === "pk" ? (
          <>
            <span className="rounded bg-yellow1/10 px-1.5 py-0.5 text-[10px] font-medium text-yellow1/90">
              PK
            </span>
            <span className="text-xs font-medium text-white">
              {pkPrimitiveTitle}
            </span>
            <span className="text-xs text-grey5">({scopeName})</span>
          </>
        ) : (
          <>
            <span className="rounded bg-purple1/20 px-1.5 py-0.5 text-[10px] font-medium text-purple1">
              {translate("QMS/ANCHOR/LINK_BADGE")}
            </span>
            {activeMapping ? (
              <span className="text-xs font-medium text-white">
                {activeMapping.mapping_title}
                <span className="font-normal text-grey5">
                  {" "}
                  · {scopeName} ↔ {relatedName}
                </span>
              </span>
            ) : (
              <span className="text-xs text-grey5">({scopeName})</span>
            )}
          </>
        )}

        {hasMappings && (
          <div className="ml-auto inline-flex items-center gap-0 overflow-hidden rounded border border-grey4 text-[10px]">
            <button
              type="button"
              onClick={() => onModeChange("pk", null)}
              className={clsx(
                "px-2 py-0.5 transition-colors",
                mode === "pk"
                  ? "bg-yellow1 font-medium text-black"
                  : "text-grey5 hover:text-white",
              )}
            >
              PK
            </button>
            <button
              type="button"
              onClick={() => {
                const first = availableMappings[0]
                onModeChange("mapping", first.primitive_id)
              }}
              className={clsx(
                "px-2 py-0.5 transition-colors",
                mode === "mapping"
                  ? "bg-yellow1 font-medium text-black"
                  : "text-grey5 hover:text-white",
              )}
            >
              {translate("QMS/ANCHOR/LINK_BADGE")}
            </button>
          </div>
        )}
      </div>

      {mode === "mapping" && availableMappings.length > 1 && (
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-xs text-grey5">
            {translate("QMS/ANCHOR/LINK_LABEL")}
          </span>
          <select
            value={mappingPrimitiveId ?? ""}
            onChange={(e) => onModeChange("mapping", e.target.value)}
            className="rounded border border-grey4 bg-grey2 px-1.5 py-1 text-xs text-white"
          >
            {availableMappings.map((m) => (
              <option key={m.primitive_id} value={m.primitive_id}>
                {m.mapping_title} (→{" "}
                {scopeNames[m.related_scope_id] ?? m.related_scope_id})
              </option>
            ))}
          </select>
        </div>
      )}

      {!isExportMode && (
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-xs text-grey5">{translate("QMS/ANCHOR/COLUMN_LABEL")}</span>
          <select
            value={columnIndex}
            onChange={(e) => {
              const colIdx = Number(e.target.value)
              if (mode === "pk") onPkColumnChange(colIdx)
              else onMappingColumnChange(colIdx)
            }}
            className="font-mono rounded border border-yellow5/50 bg-grey2 px-1.5 py-1 text-xs text-white"
          >
            <option value={-1}>{translate("QMS/COMMON/NONE_OPTION")}</option>
            {headers.map((h, idx) => (
              <option
                key={idx}
                value={idx}
                disabled={usedColumnIndices.has(idx) && idx !== columnIndex}
              >
                {h || translate("QMS/ANCHOR/COLUMN_FALLBACK", { index: idx + 1 })}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function SecondaryAnchorBox({
  scopeName,
  headers,
  usedColumnIndices,
  isExportMode,
  parentScopeName,
  anchorPrimitiveTitle,
  columnIndex,
  sharedWithPrimary,
  onColumnChange,
}: SecondaryProps & {
  scopeName: string
  headers: string[]
  usedColumnIndices: Set<number>
  isExportMode: boolean
}) {
  const { translate } = useTranslation()
  return (
    <div className="my-2 rounded-lg border border-yellow5/30 bg-grey1 p-3">
      <div className="mb-1 flex items-center gap-2">
        <span className="rounded bg-yellow1/15 px-1.5 py-0.5 text-[10px] font-medium text-yellow1">
          {translate("QMS/ANCHOR/BADGE")}
        </span>
        <span className="text-xs text-grey5">
          {translate("QMS/ANCHOR/LINK_VIA", { scope: parentScopeName })}
          {" → "}
          <span className="font-medium text-white">{scopeName}</span>
        </span>
      </div>
      {isExportMode ? (
        <div className="mt-1.5 flex items-center gap-2">
          <span className="rounded bg-purple1/20 px-1.5 py-0.5 text-[10px] font-medium text-purple1">
            {translate("QMS/ANCHOR/LINK_BADGE")}
          </span>
          <span className="text-xs font-medium text-white">
            {anchorPrimitiveTitle}
          </span>
        </div>
      ) : (
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-xs text-grey5">{translate("QMS/ANCHOR/COLUMN_LABEL")}</span>
          <select
            value={columnIndex}
            disabled={sharedWithPrimary}
            onChange={(e) => onColumnChange(Number(e.target.value))}
            className={clsx(
              "font-mono rounded border bg-grey2 px-1.5 py-1 text-xs text-white",
              sharedWithPrimary
                ? "cursor-not-allowed border-grey4 opacity-70"
                : "border-yellow5/50",
            )}
          >
            <option value={-1}>{translate("QMS/COMMON/NONE_OPTION")}</option>
            {headers.map((h, idx) => (
              <option
                key={idx}
                value={idx}
                disabled={usedColumnIndices.has(idx) && idx !== columnIndex}
              >
                {h || translate("QMS/ANCHOR/COLUMN_FALLBACK", { index: idx + 1 })}
              </option>
            ))}
          </select>
          {sharedWithPrimary && (
            <span className="text-[10px] italic text-grey5">
              {translate("QMS/ANCHOR/CONTROLLED_BY_PRIMARY")}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
